// The receipt (docs/04 §8): tribute, due, held coins, progress, spins, charges, pay now, souls, log.
import { t } from '../../i18n/t';
import { Decimal } from '../../sim/decimal';
import { modsOf, parOf } from '../../state/selectors';
import { B } from '../../content/balance';
import { useGame, useUI, type LogLine } from '../../state/store';
import { controller } from '../../render';
import { Bar, Btn, KText, useFmt } from '../common';
import { tip } from '../tooltip/Tooltip';

function lineText(l: LogLine): string {
  return t(l.key, l.params);
}

export function CollectorTip({ id }: { id: string }) {
  return (
    <div className="col">
      <span className="h kw-bad">{t(`collector.${id}.name`)}</span>
      <KText text={t(`collector.${id}.rule`)} />
    </div>
  );
}

export function HexTip({ id, reel }: { id: string; reel?: number }) {
  return (
    <div className="col">
      <span className="h kw-bad">{t(`hex.${id}.name`)}</span>
      <KText text={t(`hex.${id}.desc`, { reel: (reel ?? 0) + 1 })} />
      <span className="dim">{t('ui.hex.lasts', { n: B.HEX_DURATION })}</span>
    </div>
  );
}

export function Receipt() {
  const game = useGame((g) => g);
  const displayed = useUI((u) => u.displayedCoins);
  const busy = useUI((u) => u.busy);
  const log = useUI((u) => u.log);
  const drawer = useUI((u) => u.drawer);
  const fmt = useFmt();
  const run = game.run;
  if (!run) return null;
  const mods = modsOf(game);
  const w = run.window;
  const coins = displayed !== null ? new Decimal(displayed) : run.coins;
  const canPay = run.coins.gte(w.tribute) && (run.phase === 'ready' || run.phase === 'nudge') && w.spinsLeft > 0;
  const par = parOf(game);
  const progress = w.tribute.gt(0) ? Math.min(1, coins.div(w.tribute).toNumber()) : 0;
  const recent = log.filter((l) => l.kind !== 'say' || run.size === 'bandit').slice(-4);

  return (
    <div className={`receipt interactive col drawer drawer-left ${drawer === 'receipt' ? 'open' : ''}`}>
      <div className="receipt-paper col" style={{ gap: 3 }}>
        <div className="h" style={{ textAlign: 'center' }} data-anchor="tribute">
          {t('ui.receipt.tribute', { n: w.n })}
        </div>
        {w.reckoning && (
          <div className="kw-bad" style={{ textAlign: 'center' }} {...tip(<CollectorTip id={w.reckoning} />)} data-testid="reckoning">
            ⚠ {t(`collector.${w.reckoning}.name`)}
          </div>
        )}
        {w.reckoning && w.rules?.length && w.rules[0] !== w.reckoning ? (
          <div className="kw-bad" style={{ textAlign: 'center' }} {...tip(<CollectorTip id={w.rules[0]!} />)}>
            {t('ui.reckoning.nextRule', { name: t(`collector.${w.rules[0]}.name`) })}
          </div>
        ) : null}
        <div className="sep" />
        <div className="row" {...tip(<span>{t('ui.receipt.par', { par: par ? fmt(par) : '-' })}</span>)}>
          <span>{t('ui.receipt.due')}</span>
          <span className="h">{fmt(w.tribute)}</span>
        </div>
        <div className="row" data-anchor="coins">
          <span>{t('ui.receipt.held')}</span>
          <span className="h" style={{ color: coins.gte(w.tribute) ? '#1ebc73' : undefined }}>
            {fmt(coins)}
          </span>
        </div>
        <Bar value={progress} max={1} color={progress >= 1 ? 'clover' : 'gold'} />
        <div className="row" data-anchor="spins">
          <span>{t('ui.receipt.spins')}</span>
          <span className="h" style={{ color: w.spinsLeft <= 2 ? '#e83b3b' : undefined }}>
            {w.spinsLeft}/{w.spinsTotal}
          </span>
        </div>
        {w.counters.twinsAt ? (
          <div className="kw-bad" style={{ textAlign: 'center' }}>
            {w.counters.twinsPaid ? t('ui.reckoning.twinsPaid') : t('ui.reckoning.twins', { n: w.counters.twinsAt, due: fmt(w.tribute.div(2).ceil()) })}
          </div>
        ) : null}
        {run.nightShift && run.nightShift.left > 0 && (
          <div style={{ textAlign: 'center', color: '#6b3e75' }}>{t('ui.night.left', { n: run.nightShift.left })}</div>
        )}
        {(mods.features.has('hold') || mods.features.has('nudge')) && (
          <div className="row" style={{ justifyContent: 'center' }}>
            {t('ui.receipt.charges', { hold: run.charges.hold, nudge: run.charges.nudge })}
          </div>
        )}
        <div className="col" data-anchor="hexes" style={{ gap: 2 }}>
          {run.hexes.map((h) => (
            <div key={h.id} className="stamp" {...tip(<HexTip id={h.id} reel={h.reel} />)}>
              {t('ui.hex.stamp', { name: t(`hex.${h.id}.name`), n: h.left })}
            </div>
          ))}
          {(run.flags.hexImmune ?? 0) > 0 && <div className="dim">{t('ui.hex.immune', { n: run.flags.hexImmune! })}</div>}
        </div>
        <div className="sep" />
        <div className="row" data-anchor="soulsPending" style={{ color: '#905ea9' }}>
          <span>{t('ui.receipt.soulsPending', { n: run.soulsPending })}</span>
        </div>
        <Btn kind={canPay ? 'primary' : 'normal'} disabled={!canPay || busy} onClick={() => controller.act({ t: 'PAY_NOW' })} testId="pay-now">
          {t('ui.payNow')}
        </Btn>
      </div>
      <div className="col" style={{ gap: 2, marginTop: 4, color: '#9babb2', flex: 1, overflow: 'hidden', justifyContent: 'flex-end' }}>
        {recent.map((l) => (
          <div key={l.id} style={{ color: l.kind === 'tip' ? '#30e1b9' : l.kind === 'say' ? '#fbb954' : undefined }}>
            <KText text={lineText(l)} />
          </div>
        ))}
      </div>
    </div>
  );
}
